import React, { createContext } from "react";
import type { AppState } from "@auth0/auth0-react";
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import type { AuthContextProps } from "./AuthContextTypes";
import { useNavigate } from "react-router-dom";
import { isStandalone } from "@/utils/environment";

interface AuthProviderProps {  
  children: React.ReactNode;
}

export const AuthContext = createContext<AuthContextProps | undefined>(
  undefined
);

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  if (isStandalone()) {
    // Only initialize Auth0 when running standalone
    const domain = import.meta.env.VITE_AUTH0_DOMAIN || "";
    const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID || "";
    const navigate = useNavigate();

    const onRedirectCallback = (appState?: AppState) => {
      const redirectTo =
        sessionStorage.getItem("redirectTo") ||
        appState?.returnTo ||
        window.location.pathname;

      sessionStorage.removeItem("redirectTo");
      navigate(redirectTo);
    };

    return (
      <Auth0Provider
        domain={domain}
        clientId={clientId}
        useRefreshTokens={true}
        authorizationParams={{
          redirect_uri: window.location.origin,
          ...(import.meta.env.VITE_AUTH0_AUDIENCE && {
            audience: import.meta.env.VITE_AUTH0_AUDIENCE,
          }),
          scope: import.meta.env.VITE_AUTH_SCOPES,
        }}
        cacheLocation="localstorage"
        onRedirectCallback={onRedirectCallback}
      >
        <AuthContextProvider>{children}</AuthContextProvider>
      </Auth0Provider>
    );
  }

  // When federated, just provide the context without Auth0 initialization
  return <AuthContextProvider>{children}</AuthContextProvider>;
};

const AuthContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const auth0 = useAuth0(); // This will work in both modes

  // When federated, auth0 context comes from host
  // When standalone, auth0 context comes from local Auth0Provider

  return (
    <AuthContext.Provider value={auth0}>
      {children}
    </AuthContext.Provider>
  );
};
